import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Field } from "../../components/site/ProfileFields";
import { Logo } from "../../components/site/Logo";
import { Img, GEN } from "../../components/site/Img";
import { useContent } from "../../lib/useFetch";

export const AuthShell = ({ children, imgKey = "login_side", fallback = GEN.ilca }) => {
  const c = useContent();
  return (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden overflow-hidden lg:block">
      <Img src={c.images?.fields?.[imgKey]} fallback={fallback} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/30 to-transparent" />
      <div className="absolute bottom-12 left-12 right-12 text-white"><p className="font-display text-4xl font-extrabold leading-tight">Our goals are your medals.</p></div>
    </div>
    <div className="flex flex-col px-6 py-10 sm:px-12">
      <Logo />
      <div className="flex flex-1 items-center"><div className="w-full max-w-xl">{children}</div></div>
    </div>
  </div>
  );
};

export default function Login() {
  const { t } = useLang();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [ch, setCh] = useState(null);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const done = (data) => {
    const map = JSON.parse(localStorage.getItem("aa_trusted") || "{}");
    if (data.device_token) localStorage.setItem("aa_trusted", JSON.stringify({ ...map, [f.email.toLowerCase()]: data.device_token }));
    const u = signIn(data);
    nav(sp.get("next") || (u.role === "admin" ? "/admin" : u.role === "staff" ? "/admin/registrants" : u.role === "collaborator" ? "/admin/proforma" : "/account"));
  };
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err));
      if (err.response?.status === 400) { setCh(null); setCode(""); }
    } finally {
      setBusy(false);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const device_token = JSON.parse(localStorage.getItem("aa_trusted") || "{}")[f.email.toLowerCase()] || "";
      const { data } = await api.post("/auth/login", { ...f, device_token });
      if (data.token) return done(data);
      setCh(data);
    });
  };
  const verify = (e) => {
    e.preventDefault();
    run(async () => done((await api.post("/auth/login/verify", { challenge_id: ch.challenge_id, code, trust_device: trust })).data));
  };
  const resend = () => run(async () => { setCh((await api.post("/auth/login/resend", { challenge_id: ch.challenge_id })).data); toast.success(t("tfa_resent")); });
  if (ch) return (
    <AuthShell>
      <form onSubmit={verify} className="space-y-5" data-testid="tfa-form">
        <h1 className="font-display text-4xl font-extrabold text-[#0A2540]">{t("tfa_title")}</h1>
        <p className="text-sm text-slate-600" data-testid="tfa-sent-to">{ch.email_status === "sent" ? t("tfa_sent") : t("tfa_email_failed")} <b>{ch.email_masked}</b></p>
        <Field label={t("tfa_code")}><Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-12 rounded-xl text-center font-mono text-2xl tracking-[0.5em]" data-testid="tfa-code-input" /></Field>
        <label className="flex items-start gap-3 text-sm text-slate-600">
          <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#FF5722]" data-testid="tfa-trust-device" />
          <span>{t("tfa_trust")}</span>
        </label>
        <button disabled={busy || code.length !== 6} className="btn-buoy w-full justify-center disabled:opacity-60" data-testid="tfa-submit-button">{t("tfa_verify")}</button>
        <div className="flex justify-between text-sm">
          <button type="button" onClick={resend} disabled={busy} className="font-semibold text-[#00A8B5]" data-testid="tfa-resend">{t("tfa_resend")}</button>
          <button type="button" onClick={() => { setCh(null); setCode(""); }} className="text-slate-500" data-testid="tfa-back">{t("tfa_back")}</button>
        </div>
      </form>
    </AuthShell>
  );
  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <h1 className="font-display text-4xl font-extrabold text-[#0A2540]">{t("sign_in")}</h1>
        <Field label={t("email")}><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12 rounded-xl" data-testid="login-email-input" /></Field>
        <Field label={t("password")}><Input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12 rounded-xl" data-testid="login-password-input" /></Field>
        <button disabled={busy} className="btn-buoy w-full justify-center disabled:opacity-60" data-testid="login-submit-button">{t("sign_in")}</button>
        <p className="text-sm text-slate-500">{t("no_account")} <Link to={`/register${sp.get("next")?.startsWith("/clinics/") ? `?event=${sp.get("next").split("/")[2]}` : ""}`} className="font-semibold text-[#FF5722]" data-testid="login-to-register">{t("register")}</Link></p>
        <Link to="/forgot-password" className="block text-sm font-semibold text-[#00A8B5]" data-testid="login-forgot-password">{t("forgot_password")}</Link>
      </form>
    </AuthShell>
  );
}
